// The certificate file, seen in the app (Amit, 27 Oct 2026: "I want an in-app viewer").
//
// The server kept the file it read (rule 22: write-once in Blob, named by SHA-256) and streams it
// back through GET /cofas/{id}/file to a signed-in reader (RECEIVE, RELEASE or CAPTURE) - the
// container is never public, so an <iframe src> to it could not work and must not. The bytes are
// read with the bearer token (api.blob) and shown from an object URL in a side sheet: a PDF or an
// image inline, a Word file as a download (browsers do not render .docx). Rule 15: a read that
// failed says so and offers Try again; a certificate the server kept no file for says that, and
// where the file is (the qa@ email), instead of showing nothing.
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { api, errorMessage } from '../../lib/api';
import type { Certificate } from './goodsInModel';

/** Shown in the sheet: PDFs and images; everything else is downloaded. */
const INLINE_TYPES = new Set(['application/pdf', 'image/png', 'image/jpeg', 'image/tiff']);

/** What the sheet says the file is: the attachment's name, else the certificate's number. */
export function fileLabel(c: Pick<Certificate, 'cofaId' | 'source'>): string {
  return c.source.attachmentName ?? `certificate-${c.cofaId}`;
}

export function CertificateFile({ c }: { c: Pick<Certificate, 'cofaId' | 'source'> }) {
  const [open, setOpen] = useState(false);
  const kept = c.source.sha256 !== null;
  if (!kept) {
    return (
      <span className="text-(length:--fs-xs) text-(--muted)" title="Recorded by a person reading it, before files were kept, or the file could not be read.">
        No file kept here · see the email in {c.source.mailbox ?? 'qa@'}
      </span>
    );
  }
  return (
    // The trigger sits inside the Sheet so Radix returns focus to it on close (found live 27 Oct 2026:
    // a button outside the Sheet left focus on the page after Escape).
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button type="button" variant="outline" size="sm">
          View certificate
        </Button>
      </SheetTrigger>
        <SheetContent side="right" className="flex w-full flex-col gap-(--s2) sm:max-w-[min(1000px,92vw)]">
          <SheetHeader>
            <SheetTitle>Certificate #{c.cofaId}</SheetTitle>
            <SheetDescription className="font-mono">{fileLabel(c)}</SheetDescription>
          </SheetHeader>
          {open ? <FileViewer path={`/cofas/${c.cofaId}/file`} label={fileLabel(c)} title={`Certificate #${c.cofaId}`} /> : null}
        </SheetContent>
    </Sheet>
  );
}

type Loaded = { url: string; type: string };

/** A file the API streams, read with the bearer token and shown from an object URL (also the scan
 *  beside "Record by hand", GET /cofa-intake/{id}/file). */
export function FileViewer({ path, label, title }: { path: string; label: string; title: string }) {
  const [file, setFile] = useState<Loaded | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    const ctrl = new AbortController();
    let made: string | null = null;
    setFailed(null);
    setFile(null);
    api
      .blob(path, ctrl.signal)
      .then((b) => {
        made = URL.createObjectURL(b);
        setFile({ url: made, type: b.type });
      })
      .catch((err) => {
        if (!ctrl.signal.aborted) setFailed(errorMessage(err));
      });
    return () => {
      ctrl.abort();
      if (made) URL.revokeObjectURL(made);
    };
  }, [path, attempt]);

  if (failed) {
    return (
      <div role="alert" className="flex flex-col gap-(--s2) text-(length:--fs-sm)">
        <p className="m-0">The file could not be read: {failed}</p>
        <div>
          <Button type="button" variant="outline" size="sm" onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </Button>
        </div>
      </div>
    );
  }
  if (!file) {
    return (
      <p role="status" className="m-0 text-(length:--fs-sm) text-(--muted)">
        Loading the file…
      </p>
    );
  }
  const name = label;
  const download = (
    <a href={file.url} download={name} className="text-(length:--fs-sm) underline">
      Download {name}
    </a>
  );
  if (!INLINE_TYPES.has(file.type)) {
    return (
      <div className="flex flex-col gap-(--s2) text-(length:--fs-sm)">
        <p className="m-0">This file type ({file.type || 'unknown'}) cannot be shown in the browser. Download it to open it in Word.</p>
        <div>{download}</div>
      </div>
    );
  }
  // The sheet covers the checks table the file is read against (UI/UX audit, 31 Oct 2026, UX-26), so
  // the same object URL also opens in a tab of its own, to sit beside the receipt. Still the bytes the
  // API streamed with the bearer token: never a Blob URL, which is not public (rule 22).
  const newTab = (
    <a href={file.url} target="_blank" rel="noopener noreferrer" className="text-(length:--fs-sm) underline">
      Open in new tab<span className="sr-only">: {name}</span>
    </a>
  );
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-(--s2)">
      <div className="flex flex-wrap items-center gap-x-(--s3) gap-y-(--s1)">
        {newTab}
        {download}
      </div>
      {file.type === 'application/pdf' ? (
        <iframe
          src={file.url}
          title={`${title}: ${name}`}
          className="min-h-0 w-full flex-1 rounded-(--radius) border border-(--border)"
        />
      ) : (
        <img src={file.url} alt={`${title}: ${name}`} className="max-h-full w-full rounded-(--radius) border border-(--border) object-contain" />
      )}
    </div>
  );
}
